import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import { ContentUpgrades } from "@swizec/gatsby-theme-course-platform";
export const _frontmatter = {
  "title": "How do you know if serverless fits your project?",
  "description": "The hallmark of a true expert is understanding about the pros and cons of your technical decisions.",
  "published": "2021-03-25T00:00:00.000Z",
  "categories": "Mindset, Serverless",
  "content_upgrade": "ServerlessHandbook",
  "hero": "./img/screenshot-1616681329753.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <p>{`The hallmark of a true expert is understanding about the pros and cons of your technical decisions.`}</p>
    <p>{`You get an ambiguous problem. How do you solve it?`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Interviewing tip for senior engineers 👉 get comfortable with ambiguity.`}<br parentName="p"></br><br parentName="p"></br>{`"Design a system to solve this problem"`}<br parentName="p"></br><br parentName="p"></br>{`The interviewer can think of 5 ways of solving, 10+ exist. Your job is to think of 3, explain their tradeoffs, and ask biz questions to make a decision.`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1374847492320288780"
      }}>{`March 24, 2021`}</a></blockquote>
    <p>{`System design is the interview question that "senior" engineers struggle with the most. There's no right answer and they fall apart.`}</p>
    <p>{`It's kinda fun to watch 😈`}</p>
    <p>{`The trick to owning a decision is to understand:`}</p>
    <ol>
      <li parentName="ol">{`Many approaches solve the problem`}</li>
      <li parentName="ol">{`There are tradeoffs`}</li>
      <li parentName="ol">{`Best choice depends on context`}</li>
    </ol>
    <p>{`That's why my new book, `}<a parentName="p" {...{
        "href": "https://serverlesshandbook.dev/"
      }}>{`Serverless Handbook`}</a>{`, focuses on giving you the tools to make your own decisions.`}</p>
    <ContentUpgrades.ServerlessHandbook />
    <p>{`Like Chapter 2 on the pros & cons of serverless that concludes with a heuristic I like to use 👇`}</p>
    <h1 {...{
      "id": "serverless-pros--cons--when-should-you-go-serverless"
    }}>{`Serverless Pros & Cons – when should you go serverless?`}</h1>
    <p><a parentName="p" {...{
        "href": "https://serverlesshandbook.dev/serverless-pros-cons/"
      }}><span parentName="a" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px",
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "78.9237668161435%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'316\\'%20viewBox=\\'0%200%20400%20316\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M115%2021c-7%202-10%206-10%2012%200%205%200%205-2%204-3-2-5%200-5%204v3H71l-26%201v227h172v4c0%207%205%2013%209%2011%201-1%201%200%201%203%200%209%206%2019%2011%2019%204%200%205-2%206-14l1-11%2015%209%2017%209%2027-15%2026-14%2013-1h13v-40c0-38%200-40%202-42%207-8%207-23%201-36l-3-5V45l-108-1H141v-8c0-9-1-11-6-14-5-2-15-2-20-1m136%2083c-6%202-14%2010-18%2019l-5%208c-5%201-14%2015-16%2023l-1%203a535%20535%200%2001-40-23l-1%208c0%208%200%208-3%2010-10%208-32%207-40%200-3-3-3-3-3-11v-9h-4l-6%201c-1%203-9%205-16%205-13%200-18-3-18-11l-1-5-16%2010%2031%2018%2077%2045%2046%2026%2026-14%2027-15-13-9-14-8%2018-10c2%200%202%202%201%205-2%204-2%204%202%208l4%205h6l7-3c1-2%202-2%2012%203%2011%207%2014%208%2019%206%208-3%2010-15%206-27-2-6-2-6%200-9s-2-10-6-10c-2%200-5-1-7-3-7-5-11-7-15-7-4%201-4%200-4-2%200-8-5-14-13-13-4%201-4%201-4-2%200-9-9-15-18-12m46%2012c-3%204-3%207%200%2010%204%205%2012%202%2012-5%200-4-9-8-12-5m-143%2093l-9%2017%2039%2023-19-57-11%2017m154-3l-3%203h-5c-3-2-3-2-15%205a2888%202888%200%2001-39%2023l16%2010%2015%209%2019-11a3630%203630%200%200133-19l-6-4-7-4%201-4c1-3%201-3-2-6l-3-3-4%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/08b1c2240d57f9d386a81eb01259285b/ca0a1/tDNBrFpng6ghei7.webp 223w", "/static/08b1c2240d57f9d386a81eb01259285b/75680/tDNBrFpng6ghei7.webp 445w", "/static/08b1c2240d57f9d386a81eb01259285b/8d1ba/tDNBrFpng6ghei7.webp 890w", "/static/08b1c2240d57f9d386a81eb01259285b/131f1/tDNBrFpng6ghei7.webp 900w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/08b1c2240d57f9d386a81eb01259285b/e92b6/tDNBrFpng6ghei7.png 223w", "/static/08b1c2240d57f9d386a81eb01259285b/e66bf/tDNBrFpng6ghei7.png 445w", "/static/08b1c2240d57f9d386a81eb01259285b/4ef49/tDNBrFpng6ghei7.png 890w", "/static/08b1c2240d57f9d386a81eb01259285b/1cfc2/tDNBrFpng6ghei7.png 900w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/08b1c2240d57f9d386a81eb01259285b/4ef49/tDNBrFpng6ghei7.png",
              "alt": "tDNBrFpng6ghei7",
              "title": "tDNBrFpng6ghei7",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
    `}</span></a></p>
    <p>{`Okay you've heard of serverless, tried it out, and you think it's neat. But should you `}<em parentName="p">{`really`}</em>{` go serverless for your next project?`}</p>
    <p>{`Yes!`}</p>
    <p>{`Most of the time ...`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media4.giphy.com/media/l41YyrtIMFcxYayDS/giphy-loop.mp4?cid=7cf28b0fp5thx91gff48upsacfawbfzunb9y3xqektyxmbto&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`Serverless is a great option for most projects most of the time. You save configuration and maintenance time, gain flexibility, and in extreme cases spend more $$ per request than building your own servers.`}</p>
    <p>{`Large apps can reach the cost curve limits of serverless. Bank of America, for example, `}<a parentName="p" {...{
        "href": "https://www.businessinsider.com/bank-of-americas-350-million-internal-cloud-bet-striking-payoff-2019-10"
      }}>{`announced `}{`\\`}{`$2B in savings`}</a>{` from building their own data centers.`}</p>
    <p>{`You won't hit those issues. And if you do, I hope there's a business model to back it up and you can afford DevOps professionals. 😛`}</p>
    <p>{`Large orgs tend to provide a cloud or serverless-like environment internally. If you have access to that, use it.`}</p>
    <h2 {...{
      "id": "serverless-is-an-ecosystem"
    }}>{`Serverless is an ecosystem`}</h2>
    <p>{`When I say serverless, I don't mean just throwing up code on a `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Function_as_a_service"
      }}>{`function-as-a-service`}</a>{` platform like AWS Lambda. I'm talking about the whole ecosystem.`}</p>
    <p>{`The core idea is this:`}</p>
    <ol>
      <li parentName="ol">{`Backend does as little as possible`}</li>
      <li parentName="ol">{`Clients tie everything together`}</li>
      <li parentName="ol">{`Static files come from fast `}<a parentName="li" {...{
          "href": "https://en.wikipedia.org/wiki/Content_delivery_network"
        }}>{`content delivery networks`}</a></li>
      <li parentName="ol">{`Database handles data consistency`}</li>
      <li parentName="ol">{`As much work as possible happens at compile and deploy time`}</li>
    </ol>
    <p>{`Users' and developers' machines do the hard work.`}</p>
    <p>{`Is part of your app the same for every user? Package it up at deploy time. No need to bother the server `}<em parentName="p">{`or`}</em>{` the client with that work.`}</p>
    <p>{`Is part of your app specific to individual users? Let the client handle it. Every phone is a powerful computer these days.`}</p>
    <p>{`Got dynamic data that needs to synchronize across multiple sessions or users? Background processes that aren't tied to a specific session? Perfect fit for your server and database.`}</p>
    <p>{`We go in depth about this architecture in the chapter on `}<a parentName="p" {...{
        "href": "/serverless-architecture-principles"
      }}>{`Serverless Architecture Principles`}</a>{`.`}</p>
    <h2 {...{
      "id": "serverless-pros"
    }}>{`Serverless pros`}</h2>
    <p>{`The main benefit of serverless is that you don't deal with servers. They're somebody else's problem.`}</p>
    <h3 {...{
      "id": "you-save-time"
    }}>{`You save time`}</h3>
    <p>{`You focus on `}<em parentName="p">{`application`}</em>{` code. No more tedious maintenance tasks that aren't specific to your problem.`}</p>
    <p>{`Bye bye yak shaving. 👋`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media4.giphy.com/media/Gd0A7bNRh5UNW/giphy-loop.mp4?cid=7cf28b0fiug51hlafxrt9zkzjni3hs2ec8lmpik0pl9hlmzn&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p><em parentName="p">{`"I need an API. That means I have to run a server. Which means I need Apache or Nginx to map HTTP requests to my app server. I need a computer to run all that. Which means I have to set up a whole operating system. Then I have to make sure everything runs at boot. And if a process goes down, it needs to restart. And ..."`}</em></p>
    <p>{`After all that work you get to build your application.`}</p>
    <p>{`With serverless `}<strong parentName="p">{`you save time otherwise spent managing servers.`}</strong>{` Whether that's you personally or a DevOps team in your organization.`}</p>
    <h3 {...{
      "id": "programming-productivity"
    }}>{`Programming productivity`}</h3>
    <p>{`You write backend code more productively.`}</p>
    <p>{`Smaller, more self-contained code, ideally a single function, brings clarity and focus. `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Unix_philosophy#Do_One_Thing_and_Do_It_Well"
      }}>{`Do one thing and do it well`}</a></p>
    <p>{`With increased focus, you get:`}</p>
    <ul>
      <li parentName="ul">{`easier testing`}</li>
      <li parentName="ul">{`quicker understanding`}</li>
      <li parentName="ul">{`shorter development cycles`}</li>
    </ul>
    <h3 {...{
      "id": "often-cheaper"
    }}>{`Often cheaper`}</h3>
    <p>{`Serverless can be cheaper to run.`}</p>
    <p>{`You save opportunity and employee cost `}<em parentName="p">{`and`}</em>{` you're not paying for servers you aren't using.`}</p>
    <p>{`As mentioned in the `}<a parentName="p" {...{
        "href": "https://serverlesshandbook.dev/getting-started"
      }}>{`Getting Started`}</a>{` chapter: before serverless, you'd have to (over)provision a bunch of machines in case there's a traffic spike. That means you're paying for servers you aren't using.`}</p>
    <p>{`With serverless, you pay per execution and run time. Like pay-as-you-go pricing: Run code, pay for that run.`}</p>
    <p><strong parentName="p">{`When there's no traffic, there's no cost. 👌`}</strong></p>
    <h3 {...{
      "id": "scalability"
    }}>{`Scalability`}</h3>
    <p>{`Google likes to call serverless architectures `}<em parentName="p">{`‌from prototype to production to planet-scale`}</em>{`. You don't want to use serverless at planet scale though.`}</p>
    <p>{`But Google is right: Serverless scales. A lot.`}</p>
    <p>{`The details on `}<em parentName="p">{`why`}</em>{` serverless is scalable are tricky to get into. It has to do with how much work you can pack into a single physical machine ... but there `}<em parentName="p">{`is`}</em>{` a machine somewhere and you might run out of those with truly planet-scale work 🤔`}</p>
    <p>{`It comes down to this: You're running a `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Elasticity_(cloud_computing)"
      }}>{`hyper-elastic server`}</a>{` that adapts to changes in workload at millisecond precision. In theory this gives you perfect utilization.`}</p>
    <h2 {...{
      "id": "serverless-cons"
    }}>{`Serverless cons`}</h2>
    <p>{`As much as I think serverless is the next big thing in web development, it's not all fun and games out there. There `}<em parentName="p">{`are`}</em>{` disadvantages to using serverless.`}</p>
    <h3 {...{
      "id": "higher-latency-for-low-workloads"
    }}>{`Higher latency for low workloads`}</h3>
    <p>{`Performance comes in two flavors:`}</p>
    <ol>
      <li parentName="ol">{`Latency`}</li>
      <li parentName="ol">{`Speed or bandwidth`}</li>
    </ol>
    <p>{`Latency talks about how long it takes from making a request to getting a response. Speed talks about how long it takes to do work.`}</p>
    <p>{`Each `}<em parentName="p">{`execution`}</em>{` is fast because the code is small and servers are fast. A few milliseconds and you're done.`}</p>
    <p>{`But `}<em parentName="p">{`latency`}</em>{` can be high. You're hitting the server cold every time. That means each request waits for the computer to wake up.`}</p>
    <p>{`That's why providers keep servers live between requests. But only if requests come often enough.`}</p>
    <p><strong parentName="p">{`For low traffic applications with low latency demands, you might need a constantly provisioned server.`}</strong></p>
    <h3 {...{
      "id": "sometimes-costly"
    }}>{`Sometimes costly`}</h3>
    <p>{`As `}<a parentName="p" {...{
        "href": "https://www.businessinsider.com/bank-of-americas-350-million-internal-cloud-bet-striking-payoff-2019-10"
      }}>{`Bank of America found out`}</a>{` pay-as-you-go pricing gets expensive when used a lot.`}</p>
    <p>{`Serverless providers charge based on number of requests and resources used. You pay for every request and every millisecond of computation. Known as "compute".`}</p>
    <p>{`If you have a lot of requests or long runtimes, you can rack up the costs beyond what you'd pay with your own servers.`}</p>
    <p>{`For example: You wouldn't want to train a machine learning model on a serverless architecture. Learned that painful lesson with my first startup in 2010 and GoogleAppEngine. Flicked the On switch and the credit card melted 🔥`}</p>
    <p>{`Another bad case for serverless are high traffic applications. At millions of requests per second, you're better off on your own.`}</p>
    <p><strong parentName="p">{`Serverless becomes expensive at high loads`}</strong>{`. Where the balance tips depends on what you're doing, how much time you're saving, and how much it costs to do yourself.`}</p>
    <h3 {...{
      "id": "vendor-lock-in"
    }}>{`Vendor lock-in`}</h3>
    <p>{`This one's simple: You're building on somebody else's infrastructure.`}</p>
    <p>{`If that infrastructure changes, you're screwed. If they crank up the price, you're screwed. If you want to move, you're screwed. If you want to deploy your own, you're screwed.`}</p>
    <p>{`You `}<em parentName="p">{`can`}</em>{` do all those things, but it's a tedious and difficult task that might break your app. You're not building features or working on your business while you migrate.`}</p>
    <p>{`Startups rarely live long enough to have this problem. Enterprises take defensive measures like multi-year contracts with strict service level agreements.`}</p>
    <p><strong parentName="p">{`Avoid building architecture agnostic code.`}</strong>{` It's hard and you're not likely to need it.`}</p>
    <h3 {...{
      "id": "systems-complexity"
    }}>{`Systems complexity`}</h3>
    <p>{`You're paying for the simplicity of your application code with system complexity. Individual functions are simpler and easier to test. Complexity comes from how they interact.`}</p>
    <p>{`We'll talk more about that in the `}<a parentName="p" {...{
        "href": "/robust-backend-design"
      }}>{`Robust Backend Design`}</a>{` chapter.`}</p>
    <h2 {...{
      "id": "the-verdict"
    }}>{`The verdict?`}</h2>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media3.giphy.com/media/RLPQyPTQ3g4pKGD80m/giphy-loop.mp4?cid=7cf28b0ftflanud91pspwu86u95zk40rwbnpduucrflo0873&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`It depends. You will have to think about this yourself :)`}</p>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/swizec"
      }}>{`Ping me on twitter`}</a>{`, if you'd like some help.`}</p>
    <p>{`I like to use a series of questions:`}</p>
    <ul>
      <li parentName="ul"><em parentName="li">{`"Will this require a lot of computation?`}</em>{` if the answer is yes, I consider building my own servers.`}</li>
      <li parentName="ul"><em parentName="li">{`"Will this have ridiculously high traffic?`}</em>{` if the answer is yes, I'd choose serverless because I hate doing DevOps. High traffic hopefully means I can afford a professional :)`}</li>
      <li parentName="ul"><em parentName="li">{`"Is this a small side project idea?"`}</em>{` serverless all the way`}</li>
      <li parentName="ul"><em parentName="li">{`"Does every request need to be served under 10ms?"`}</em>{` you're gonna have to roll your own`}</li>
    </ul>
    <p>{`What do you think, is serverless right for you?`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>
    <p>{`PS: I'll be raffling 5 signed copies when Serverless Handbook publishes on Mar 31st`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      